import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useOrganization } from '@/contexts/useOrganization'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { getStripeCustomerLink } from '@/features/HivesOverview/components/ExternalLinks'
import { HiveDataCard } from '@/features/HivesOverview/components/HiveDataCard'
import { MembersCard } from '@/features/HivesOverview/components/MembersCard'
import { isOrgNotFound } from '@/features/HivesOverview/util/isOrgNotFound'
import { LicensesCard } from '@/features/Licenses/components/LicensesCard'
import { FeaturesSections } from '@/features/Organizations/components/FeaturesSections'
import { DataIntelligenceUsagePanel } from '@/features/Organizations/components/OrgFeatures/DataIntelligenceUsagePanel'
import { RenameOrganizationButton } from '@/features/Organizations/components/RenameOrganizationButton'
import { SalesforceAccountCard } from '@/features/SalesforceAccounts/components/SalesforceAccountCard'
import {
  useFindHivesByOrgId,
  useFindOrgById,
  useInternalApiKey,
  useOneMoreTrial,
} from '@/generated/http-clients/v2ApiComponents'
import { toTokenData } from '@/helpers/toTokenData'
import { toaster } from '@/hooks/useToast/toastUtils'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import {
  Button,
  DataList,
  Grid,
  GridItem,
  HStack,
  Icon,
  Skeleton,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import {
  Link,
  createRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import { FileText, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { hivesOverviewClusterDetailsRoute } from './HivesOverviewClusterDetails'

export const orgDetailsSearchParams = z.object({
  licensesPage: z.number().optional().default(0),
  licensesStatus: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED']).optional(),
  addMembers: z.literal(1).optional().catch(undefined),
})

export const hivesOverviewOrganizationDetailsRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/orgs/$orgId',
  component: HivesOverviewsOrganizationDetailsPage,
  validateSearch: orgDetailsSearchParams,
})

export function HivesOverviewsOrganizationDetailsPage() {
  const params = hivesOverviewOrganizationDetailsRoute.useParams()
  const navigate = useNavigate()
  const search = useSearch({ from: hivesOverviewOrganizationDetailsRoute.id })
  const licensesPage = search.licensesPage ?? 0
  const licensesStatus = search.licensesStatus

  const onAddMembersRequestHandled = useCallback(() => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, addMembers: undefined }),
      replace: true,
    })
  }, [navigate])

  const handleLicensesPageChange = (page: number) => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, licensesPage: page }),
    })
  }

  const handleLicensesStatusChange = (
    status: 'ACTIVE' | 'EXPIRED' | 'REVOKED' | undefined,
  ) => {
    void navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        licensesStatus: status,
        licensesPage: 0,
      }),
    })
  }

  const { t: tCommon } = useTranslation(undefined, {
    useSuspense: false,
  })
  const { t } = useTranslation('features', {
    useSuspense: false,
  })
  const orgContext = useOrganization()

  const { hasAdminRights, hasOperatorRights, hasGtmRights } =
    useRBACPermissions()

  const isAdmin = hasAdminRights()
  const isOperator = hasOperatorRights()

  const canAddTrial = isAdmin || isOperator
  const canViewInternalApiToken = isAdmin || isOperator
  const canManageSupportUsers = isAdmin || isOperator
  const canEditRoles = isAdmin || isOperator || hasGtmRights()

  const orgId = useMemo(() => {
    return params.orgId
  }, [params.orgId])

  useEffect(() => {
    orgContext.setSelectedOrganizationId(orgId)
  }, [orgContext.setSelectedOrganizationId, orgId])

  const hivesRequest = useFindHivesByOrgId({
    pathParams: {
      orgId: orgId,
    },
  })

  const orgNotFound = isOrgNotFound(hivesRequest)

  useEffect(() => {
    if (orgNotFound && search.addMembers === 1) {
      onAddMembersRequestHandled()
    }
  }, [orgNotFound, search.addMembers, onAddMembersRequestHandled])

  const orgRequest = useFindOrgById(
    {
      pathParams: {
        orgId: orgId,
      },
    },
    {
      enabled:
        canViewInternalApiToken && hivesRequest.isSuccess && !orgNotFound,
    },
  )

  const ownerId = orgRequest.data?.owner_id

  const internalApiKey = useInternalApiKey(
    {
      pathParams: {
        orgId: orgId,
      },
      queryParams: {
        customerId: ownerId ? btoa(ownerId) : '',
      },
    },
    {
      enabled:
        canViewInternalApiToken &&
        hivesRequest.isSuccess &&
        !orgNotFound &&
        Boolean(ownerId),
    },
  )

  const oneMoreTrial = useOneMoreTrial({
    onSuccess: () => {
      toaster.create({
        title: t('orgDetails.addTrial.success'),
        type: 'success',
      })
    },
    onError: () => {
      toaster.create({
        title: t('orgDetails.addTrial.error'),
        type: 'error',
      })
    },
  })

  const handleAddTrial = () => {
    oneMoreTrial.mutate({
      pathParams: {
        orgId: orgId,
      },
    })
  }

  const decodedToken = useMemo(() => {
    if (internalApiKey.data?.token) {
      return toTokenData(internalApiKey.data?.token)
    }
    return null
  }, [internalApiKey.data?.token])

  const orgName = hivesRequest.data?.company_name?.trim()
  const pageTitle = orgName || t('orgDetails.pageTitle')

  const breadCrumbs = [
    { name: tCommon('sidebar.runningHives'), path: hivesOverviewRootRoute.to },
    { name: orgName || orgId, path: '' },
  ]

  function routeToClusterDetails(orgId: string, clusterId: string | undefined) {
    if (clusterId) {
      navigate({
        to: hivesOverviewClusterDetailsRoute.to,
        params: {
          orgId,
          clusterId,
        },
      })
    } else {
      toaster.create({
        title: t('toast.clusterNotAccessible'),
        type: 'warning',
      })
    }
  }

  const auditLogsLink = (
    <Link to={auditLogsRoute.to} search={{ orgId, dateRange: 'last24Hours' }}>
      <Button variant="subtle" size="sm">
        <Icon as={FileText} />
        {String(t('auditLogs.contextualAccess.viewAuditLogs'))}
      </Button>
    </Link>
  )

  if (orgNotFound) {
    return (
      <PageLayout
        title={t('orgDetails.notFound.title')}
        beforeTitle={
          <BreadCrumbs
            paths={[
              {
                name: tCommon('sidebar.runningHives'),
                path: hivesOverviewRootRoute.to,
              },
              { name: orgId, path: '' },
            ]}
          />
        }
        action={<HStack gap={2}>{auditLogsLink}</HStack>}
      >
        <SectionCard
          alertStatus="warning"
          alertDescription={t('orgDetails.notFound.description', { orgId })}
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={pageTitle}
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={
        <HStack gap={2}>
          {auditLogsLink}
          {hivesRequest.isSuccess && canAddTrial && (
            <Button
              colorPalette="brand"
              variant="solid"
              size="sm"
              onClick={handleAddTrial}
              loading={oneMoreTrial.isPending}
              disabled={oneMoreTrial.isPending}
            >
              <Icon as={Plus} />
              {t('orgDetails.addTrial.button')}
            </Button>
          )}
        </HStack>
      }
    >
      {hivesRequest.isLoading ? <Skeleton h="500px" w="100%" /> : null}
      {hivesRequest.isError || !hivesRequest.data ? (
        <ApiErrorState
          apiResource={hivesRequest as unknown as UseQueryResult}
          errorTitle={t('orgDetails.error.title')}
          errorDescription={t('orgDetails.error.description')}
        />
      ) : null}
      {hivesRequest.isSuccess ? (
        <>
          <Grid
            templateColumns={
              canViewInternalApiToken ? '1fr 1fr 2fr' : '1fr 2fr'
            }
            width="100%"
            alignItems="stretch"
            gap={4}
          >
            <GridItem>
              <SectionCard title={t('orgDetails.details.title')} h="full">
                <DataList.Root orientation="vertical">
                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.details.orgId')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      <CopyableValue value={hivesRequest.data?.org_id} />
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.details.orgName')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      <HStack gap={1}>
                        <CopyableValue
                          value={hivesRequest.data?.company_name}
                        />
                        <RenameOrganizationButton
                          orgId={orgId}
                          currentName={orgName ?? ''}
                        />
                      </HStack>
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.details.stripeId')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      <CopyableValue
                        value={hivesRequest.data?.stripe_id}
                        externalLink={
                          hivesRequest.data?.stripe_id
                            ? getStripeCustomerLink(hivesRequest.data.stripe_id)
                            : undefined
                        }
                      />
                    </DataList.ItemValue>
                  </DataList.Item>
                </DataList.Root>
              </SectionCard>
            </GridItem>
            {canViewInternalApiToken && (
              <GridItem>
                <SectionCard
                  title={t('orgDetails.internalApiToken.title')}
                  h="full"
                >
                  <DataList.Root orientation="vertical">
                    <DataList.Item>
                      <DataList.ItemLabel>
                        {t('orgDetails.internalApiToken.token')}
                      </DataList.ItemLabel>
                      <DataList.ItemValue>
                        <CopyableValue
                          value={decodedToken?.Token}
                          maxW="200px"
                        />
                      </DataList.ItemValue>
                    </DataList.Item>

                    <DataList.Item>
                      <DataList.ItemLabel>
                        {t('orgDetails.internalApiToken.expiryDate')}
                      </DataList.ItemLabel>
                      <DataList.ItemValue>
                        <CopyableValue
                          value={decodedToken?.Expiry}
                          hasCopy={false}
                        />
                      </DataList.ItemValue>
                    </DataList.Item>
                  </DataList.Root>
                </SectionCard>
              </GridItem>
            )}
            <SectionCard key="Features" title={t('orgDetails.features.title')}>
              <FeaturesSections />
            </SectionCard>
          </Grid>
          <SalesforceAccountCard orgId={orgId} />
          <LicensesCard
            orgId={orgId}
            page={licensesPage}
            onPageChange={handleLicensesPageChange}
            licensesStatus={licensesStatus}
            onStatusChange={handleLicensesStatusChange}
          />
          {hivesRequest.data?.hives_info?.length ? (
            <SectionCard title={t('orgDetails.hives.title')}>
              <VStack align="stretch" gap={4}>
                {hivesRequest.data.hives_info.map((hive) => (
                  <HiveDataCard
                    key={hive.cluster_id}
                    hive={hive}
                    orgId={orgId}
                    stripeId={hivesRequest.data?.stripe_id}
                    routeToClusterDetails={routeToClusterDetails}
                  />
                ))}
              </VStack>
            </SectionCard>
          ) : null}

          <MembersCard
            key={orgId}
            orgId={orgId}
            canManageSupportUsers={canManageSupportUsers}
            canEditRoles={canEditRoles}
            canonicalOrgId={hivesRequest.data?.org_id}
            ownerId={orgRequest.data?.owner_id}
            addMembersRequested={search.addMembers === 1}
            onAddMembersRequestHandled={onAddMembersRequestHandled}
          />

          <DataIntelligenceUsagePanel orgId={orgId} />
        </>
      ) : null}
    </PageLayout>
  )
}
